import { isDocumentLoadStopped } from "@/lib/error-tracking/stopped-document-load";

const CHUNK_LOAD_RELOAD_PREFIX = "repackify:chunk-load-reloaded";
const CHUNK_LOAD_RECOVERY_PENDING_KEY = "repackify:chunk-load-recovery-pending";

export type ChunkLoadRecoveryOutcome =
  | "not_chunk_load_error"
  | "reloading"
  | "exhausted"
  | "storage_unavailable"
  | "load_stopped";

export type ChunkLoadReloadOutcome = Exclude<
  ChunkLoadRecoveryOutcome,
  "not_chunk_load_error" | "load_stopped"
>;

export interface ChunkLoadRecoveryConfirmation {
  attemptedPathname: string;
  renderedPathname: string;
}

interface PendingChunkLoadRecovery {
  attemptedPathname: string;
  documentId: string;
}

export interface ChunkLoadRecoveryLifecycle {
  attempt(
    error: Error,
    pathname: string,
    beforeReload?: () => void,
  ): ChunkLoadRecoveryOutcome;
  reloadOnce(
    pathname: string,
    beforeReload?: () => void,
  ): ChunkLoadReloadOutcome;
  confirm(pathname: string): ChunkLoadRecoveryConfirmation | null;
}

const CHUNK_LOAD_MESSAGE_PATTERNS = [
  /^failed to fetch dynamically imported module: https?:\/\//,
  /^failed to load chunk \/?_next\//,
  /^loading chunk \d+ failed(?:\.|\s|$)/,
  /^chunkloaderror(?::|$)/,
  /^error loading dynamically imported module: https?:\/\//,
  /^importing a module script failed\.?$/,
  /^importing a module script failed: https?:\/\//,
  /^unable to preload css for https?:\/\//,
];

const WEBPACK_MODULE_REGISTRY_MESSAGE_PATTERNS = [
  /cannot read propert(?:y|ies) of undefined \(reading '?call'?\)/,
  /undefined is not an object \(evaluating '[^']*\[[^\]]*\]\.call'\)/,
  /can['’]t access property "call", .* is undefined/,
];

const WEBPACK_RUNTIME_FRAME_MARKERS = [
  "/static/chunks/webpack-",
  "webpack/bootstrap",
];

function topStackFrame(stack: string): string | undefined {
  return stack
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.startsWith("at ") || line.includes("://"));
}

export function isStaleWebpackModuleError(error: Error): boolean {
  const message = (error.message ?? "").toLowerCase();
  if (
    !WEBPACK_MODULE_REGISTRY_MESSAGE_PATTERNS.some((pattern) =>
      pattern.test(message),
    )
  ) {
    return false;
  }
  const topFrame = topStackFrame(error.stack ?? "");
  if (topFrame === undefined) return false;
  return WEBPACK_RUNTIME_FRAME_MARKERS.some((marker) =>
    topFrame.includes(marker),
  );
}

export const DEVELOPMENT_BUILD_KEY = "development";

export function currentBuildKey(): string {
  return process.env.NEXT_PUBLIC_RELEASE_SHA || DEVELOPMENT_BUILD_KEY;
}

export function isChunkLoadErrorValue(
  value: string,
  type: string = "",
): boolean {
  const message = value.toLowerCase();
  return (
    type === "ChunkLoadError" ||
    CHUNK_LOAD_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  );
}

export function isChunkLoadError(error: Error): boolean {
  return (
    isChunkLoadErrorValue(error.message ?? "", error.name) ||
    isStaleWebpackModuleError(error)
  );
}

function chunkLoadReloadKey(pathname: string): string {
  return `${CHUNK_LOAD_RELOAD_PREFIX}:${currentBuildKey()}:${pathname}`;
}

function currentDocumentId(): string {
  return globalThis.performance?.timeOrigin.toString() ?? "unknown";
}

function readPendingRecovery(
  storage: Storage,
): PendingChunkLoadRecovery | null {
  const value = storage.getItem(CHUNK_LOAD_RECOVERY_PENDING_KEY);
  if (!value) return null;

  try {
    const pending: unknown = JSON.parse(value);
    if (
      typeof pending === "object" &&
      pending !== null &&
      "attemptedPathname" in pending &&
      "documentId" in pending &&
      typeof pending.attemptedPathname === "string" &&
      typeof pending.documentId === "string"
    ) {
      return {
        attemptedPathname: pending.attemptedPathname,
        documentId: pending.documentId,
      };
    }
  } catch {
    return { attemptedPathname: value, documentId: "legacy" };
  }

  return null;
}

function claimReloadAttempt(
  storage: Storage,
  documentId: string,
  pathname: string,
): ChunkLoadReloadOutcome | "claimed" {
  try {
    const pending = readPendingRecovery(storage);
    if (pending?.documentId === documentId) return "reloading";

    const reloadKey = chunkLoadReloadKey(pathname);
    const reloadedOnThisBuild = storage.getItem(reloadKey) === "1";
    const reloadedWithoutRecovering =
      pending !== null && pending.attemptedPathname === pathname;
    if (reloadedOnThisBuild || reloadedWithoutRecovering) {
      storage.removeItem(CHUNK_LOAD_RECOVERY_PENDING_KEY);
      return "exhausted";
    }
    storage.setItem(reloadKey, "1");
    storage.setItem(
      CHUNK_LOAD_RECOVERY_PENDING_KEY,
      JSON.stringify({ attemptedPathname: pathname, documentId }),
    );
    return "claimed";
  } catch {
    return "storage_unavailable";
  }
}

export function createChunkLoadRecoveryLifecycle(
  storage: Storage,
  reload: () => void,
  documentId: string = currentDocumentId(),
): ChunkLoadRecoveryLifecycle {
  const reloadOnce: ChunkLoadRecoveryLifecycle["reloadOnce"] = (
    pathname,
    beforeReload,
  ) => {
    const claim = claimReloadAttempt(storage, documentId, pathname);
    if (claim !== "claimed") return claim;
    try {
      beforeReload?.();
    } catch {}
    reload();
    return "reloading";
  };

  return {
    attempt(error, pathname, beforeReload) {
      if (!isChunkLoadError(error)) return "not_chunk_load_error";
      return reloadOnce(pathname, beforeReload);
    },
    reloadOnce,
    confirm(pathname) {
      try {
        const pending = readPendingRecovery(storage);
        if (!pending) return null;
        if (pending.documentId === documentId) return null;
        storage.removeItem(CHUNK_LOAD_RECOVERY_PENDING_KEY);
        return {
          attemptedPathname: pending.attemptedPathname,
          renderedPathname: pathname,
        };
      } catch {
        return null;
      }
    },
  };
}

export function confirmChunkLoadRecovery(
  pathname: string,
): ChunkLoadRecoveryConfirmation | null {
  if (typeof window === "undefined") return null;

  try {
    return createChunkLoadRecoveryLifecycle(
      window.sessionStorage,
      () => window.location.reload(),
    ).confirm(pathname);
  } catch {
    return null;
  }
}

export function attemptChunkLoadRecovery(
  error: Error,
  beforeReload?: () => void,
): ChunkLoadRecoveryOutcome {
  if (!isChunkLoadError(error)) return "not_chunk_load_error";
  if (typeof window === "undefined") return "storage_unavailable";
  if (isDocumentLoadStopped()) return "load_stopped";

  try {
    return createChunkLoadRecoveryLifecycle(
      window.sessionStorage,
      () => window.location.reload(),
    ).attempt(error, window.location.pathname, beforeReload);
  } catch {
    return "storage_unavailable";
  }
}
